import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '../hooks/useTranslation';
import {
  faNode,
  faGitAlt,
  faLinux,
  faJira,
  faHackerNews,
  faPython,
  faCuttlefish,
  faMicrosoft,
} from '@fortawesome/free-brands-svg-icons';
import {
  faDatabase,
  faTools,
  faCogs,
  faChartBar,
  faRobot,
  faWrench,
  faProjectDiagram,
  faFileCode,
  faLaptopCode,
  faNetworkWired,
  faClipboardCheck,
  faRunning,
  faIndustry,
  faTerminal,
  faCalculator,
  faClipboardList,
  faChartLine,
  faHashtag,
  faBoxes,
  faToolbox,
  faPencilAlt,
  faChartPie,
  faSearch,
  faBullseye,
  faClipboard,
  faSortAmountUp,
  faArrowUp,
  faTachometerAlt,
  faHistory
} from '@fortawesome/free-solid-svg-icons';
import '../styles/TechSkills.css';

const TechSkills = () => {
  // State to track which skill is hovered
  const [activeSkill, setActiveSkill] = useState(null);
  const { t } = useTranslation();

  const categories = {
    // Primary maintenance skills first
    [t('techSkills.maintenanceSkills')]: [
      {
        name: t('techSkillsList.maintenance.system.name'),
        icon: faWrench,
        color: '#FF6B6B',
        details: t('techSkillsList.maintenance.system.details')
      },
      {
        name: t('techSkillsList.maintenance.reliability.name'),
        icon: faHistory,
        color: '#3498DB',
        details: t('techSkillsList.maintenance.reliability.details')
      },
      {
        name: t('techSkillsList.maintenance.predictive.name'),
        icon: faSearch,
        color: '#9B59B6',
        details: t('techSkillsList.maintenance.predictive.details')
      },
      {
        name: t('techSkillsList.maintenance.tpm.name'),
        icon: faBullseye,
        color: '#F39C12',
        details: t('techSkillsList.maintenance.tpm.details')
      },
      {
        name: t('techSkillsList.maintenance.troubleshooting.name'),
        icon: faToolbox,
        color: '#6E5494',
        details: t('techSkillsList.maintenance.troubleshooting.details')
      },
      {
        name: t('techSkillsList.maintenance.writing.name'),
        icon: faPencilAlt,
        color: '#4B8B3B',
        details: t('techSkillsList.maintenance.writing.details')
      }
    ],

    // GMAO & ERP systems directly support maintenance
    [t('techSkills.gmaoSystems')]: [
      {
        name: t('techSkillsList.gmao.sap.name'),
        icon: faCogs,
        color: '#0FAAFF',
        details: t('techSkillsList.gmao.sap.details')
      },
      {
        name: t('techSkillsList.gmao.dimomaint.name'),
        icon: faIndustry,
        color: '#4A6FB5',
        details: t('techSkillsList.gmao.dimomaint.details')
      },
      {
        name: t('techSkillsList.gmao.cegid.name'),
        icon: faBoxes,
        color: '#FF5733',
        details: t('techSkillsList.gmao.cegid.details')
      },
      {
        name: t('techSkillsList.gmao.aqmanager.name'),
        icon: faClipboardList,
        color: '#33A1DE',
        details: t('techSkillsList.gmao.aqmanager.details')
      },
    ],

    // Performance monitoring is critical for maintenance
    [t('techSkills.performanceMonitoring')]: [
      {
        name: t('techSkillsList.performance.kpi.name'),
        icon: faTachometerAlt,
        color: '#F1C40F',
        details: t('techSkillsList.performance.kpi.details')
      },
      {
        name: t('techSkillsList.performance.dashboards.name'),
        icon: faChartBar,
        color: '#3498DB',
        details: t('techSkillsList.performance.dashboards.details')
      }
    ],

    // Continuous improvement methodologies applied to maintenance
    [t('techSkills.continuousImprovement')]: [
      {
        name: t('techSkillsList.continuousImprovement.lean.name'),
        icon: faSortAmountUp,
        color: '#27AE60',
        details: t('techSkillsList.continuousImprovement.lean.details')
      },
      {
        name: t('techSkillsList.continuousImprovement.5s.name'),
        icon: faClipboard,
        color: '#16A085',
        details: t('techSkillsList.continuousImprovement.5s.details')
      },
      {
        name: t('techSkillsList.continuousImprovement.kaizen.name'),
        icon: faArrowUp,
        color: '#2ECC71',
        details: t('techSkillsList.continuousImprovement.kaizen.details')
      },
      {
        name: t('techSkillsList.continuousImprovement.pareto.name'),
        icon: faChartPie,
        color: '#E74C3C',
        details: t('techSkillsList.continuousImprovement.pareto.details')
      }
    ],

    // Project management for maintenance projects
    [t('techSkills.projectManagement')]: [
      {
        name: t('techSkillsList.projectManagement.jira.name'),
        icon: faJira,
        color: '#0052CC',
        details: t('techSkillsList.projectManagement.jira.details')
      },
      {
        name: t('techSkillsList.projectManagement.agile.name'),
        icon: faClipboardCheck,
        color: '#83B81A',
        details: t('techSkillsList.projectManagement.agile.details')
      },
      {
        name: t('techSkillsList.projectManagement.sprint.name'),
        icon: faRunning,
        color: '#6554C0',
        details: t('techSkillsList.projectManagement.sprint.details')
      },
    ],

    // Design & Engineering skills related to maintenance
    [t('techSkills.designEngineering')]: [
      {
        name: t('techSkillsList.design.fusion.name'),
        icon: faTools,
        color: '#FF9E0F',
        details: t('techSkillsList.design.fusion.details')
      },
      {
        name: t('techSkillsList.design.arduino.name'),
        icon: faHackerNews,
        color: '#00979D',
        details: t('techSkillsList.design.arduino.details')
      },
      {
        name: t('techSkillsList.design.fritzing.name'),
        icon: faNetworkWired,
        color: '#D34231',
        details: t('techSkillsList.design.fritzing.details')
      },
    ],

    // Office tools for documentation and analysis
    [t('techSkills.officeProductivity')]: [
      {
        name: t('techSkillsList.office.o365.name'),
        icon: faMicrosoft,
        color: '#D83B01',
        details: t('techSkillsList.office.o365.details')
      },
      {
        name: t('techSkillsList.office.powerAutomate.name'),
        icon: faProjectDiagram,
        color: '#0077FF',
        details: t('techSkillsList.office.powerAutomate.details')
      },
      {
        name: t('techSkillsList.office.powerApps.name'),
        icon: faLaptopCode,
        color: '#742774',
        details: t('techSkillsList.office.powerApps.details')
      },
      {
        name: t('techSkillsList.office.powerBi.name'),
        icon: faChartBar,
        color: '#F2C811',
        details: t('techSkillsList.office.powerBi.details')
      },
    ],

    // Data & databases for maintenance analytics
    [t('techSkills.databasesData')]: [
      {
        name: t('techSkillsList.data.sql.name'),
        icon: faDatabase,
        color: '#336791',
        details: t('techSkillsList.data.sql.details')
      },
      {
        name: t('techSkillsList.data.dax.name'),
        icon: faCalculator,
        color: '#F2C811',
        details: t('techSkillsList.data.dax.details')
      },
      {
        name: t('techSkillsList.data.powerQuery.name'),
        icon: faChartLine,
        color: '#00B2A9',
        details: t('techSkillsList.data.powerQuery.details')
      },
    ],

    // OS & Infrastructure 
    [t('techSkills.osSystems')]: [
      {
        name: t('techSkillsList.os.debian.name'),
        icon: faLinux,
        color: '#A81D33',
        details: t('techSkillsList.os.debian.details')
      },
      {
        name: t('techSkillsList.os.bash.name'),
        icon: faTerminal,
        color: '#4EAA25',
        details: t('techSkillsList.os.bash.details')
      },
    ],

    // Programming languages for automation and analysis
    [t('techSkills.programmingLanguages')]: [
      {
        name: t('techSkillsList.programming.cpp.name'),
        icon: faCuttlefish,
        color: '#00599C',
        details: t('techSkillsList.programming.cpp.details')
      },
      {
        name: t('techSkillsList.programming.csharp.name'),
        icon: faHashtag,
        color: '#68217A',
        details: t('techSkillsList.programming.csharp.details')
      },
      {
        name: t('techSkillsList.programming.vba.name'),
        icon: faFileCode,
        color: '#217346',
        details: t('techSkillsList.programming.vba.details')
      },
      {
        name: t('techSkillsList.programming.python.name'),
        icon: faPython,
        color: '#3776AB',
        details: t('techSkillsList.programming.python.details')
      },
    ],

    // DevOps & Tools
    [t('techSkills.devOpsTools')]: [
      {
        name: t('techSkillsList.devops.git.name'),
        icon: faGitAlt,
        color: '#F05032',
        details: t('techSkillsList.devops.git.details')
      },
      {
        name: t('techSkillsList.devops.llm.name'),
        icon: faRobot,
        color: '#2496ED',
        details: t('techSkillsList.devops.llm.details')
      },
    ],

    // Web technologies (furthest from maintenance)
    [t('techSkills.webTechnologies')]: [
      {
        name: t('techSkillsList.web.node.name'),
        icon: faNode,
        color: '#339933',
        details: t('techSkillsList.web.node.details')
      },
    ],
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <section id="skills" className="tech-skills">
      {Object.entries(categories).map(([category, skills]) => (
        <div key={category} className="skill-category">
          <h3 className="category-title">{category}</h3>
          <div className="skill-list">
            {skills.map((skill, index) => (
              <motion.div
                key={skill.name}
                className="skill-bubble"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                  type: "spring",
                  stiffness: 260,
                  damping: 20
                }}
                whileHover={!isMobile ? {
                  scale: 1.1,
                  boxShadow: `0 0 20px ${skill.color}`,
                  transition: { duration: 0.2 }
                } : {}}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: `${skill.color}20`,
                  borderColor: skill.color
                }}
                onClick={() => {
                  if (isMobile) {
                    setActiveSkill(activeSkill === skill.name ? null : skill.name);
                  }
                }}
                onMouseEnter={() => !isMobile && setActiveSkill(skill.name)}
                onMouseLeave={() => !isMobile && setActiveSkill(null)}
              >
                <FontAwesomeIcon
                  icon={skill.icon}
                  style={{ color: '#f0f0f0' }}
                  className="skill-icon"
                />
                <span className="skill-name">{skill.name}</span>

                {activeSkill === skill.name && (
                  <motion.div
                    className="skill-tooltip"
                    initial={{ opacity: 0, y: 10, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, x: "-50%" }}
                    transition={{ duration: 0.2 }}
                    style={{ borderColor: skill.color, left: "50%" }}
                  >
                    <div className="tooltip-content">
                      <h4 style={{ color: skill.color }}>{skill.name}</h4>
                      <p>{skill.details}</p>
                    </div>
                    <div
                      className="tooltip-arrow"
                      style={{ borderTopColor: skill.color }}
                    ></div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

export default TechSkills;